import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useEffect } from 'react';

/* -------------------------------------------------------------
   Helpers
------------------------------------------------------------- */

// Day of year (0–365)
const getDayOfYear = date => {
    const start = new Date(date.getFullYear(), 0, 0);
    const diff = date - start;
    return Math.floor(diff / (1000 * 60 * 60 * 24));
};

// Normalize streak string → full 366-day array
const normalizeStreakString = streakString => {
    const raw = streakString.split(',').map(Number);
    const full = new Array(366).fill(0);

    for (let i = 0; i < raw.length; i++) {
        full[i] = Number.isFinite(raw[i]) ? raw[i] : 0;
    }
    return full;
};

// Convert streak data → last 20 weeks of calendar entries
const parseStreakDataFor20Weeks = streakString => {
    if (!streakString || typeof streakString !== 'string') return [];

    const points = normalizeStreakString(streakString);

    const today = new Date();
    const endDate = new Date(today);
    const startDate = new Date(today);
    const daysToShow = 20 * 7; // 20 weeks
    startDate.setDate(today.getDate() - daysToShow + 1);

    const results = [];

    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
        const dayIndex = getDayOfYear(d) - 1;
        const xp = points[dayIndex] || 0;

        let level = 0;
        if (xp > 250) level = 4;
        else if (xp > 100) level = 3;
        else if (xp > 20) level = 2;
        else if (xp > 0) level = 1;

        results.push({
            date: d.toISOString().split('T')[0],
            count: xp,
            level,
        });
    }

    return results;
};

// Build weeks in proper Sun → Sat structure
const buildWeeks = dayEntries => {
    if (!dayEntries.length) return [];

    const weeks = [];
    let currentWeek = Array(7).fill(null);

    dayEntries.forEach(entry => {
        const d = new Date(entry.date);
        const dow = d.getDay();
        currentWeek[dow] = entry;

        // End of week → push and reset
        if (dow === 6) {
            weeks.push(currentWeek);
            currentWeek = Array(7).fill(null);
        }
    });

    // Push incomplete final week
    if (currentWeek.some(x => x !== null)) {
        weeks.push(currentWeek);
    }

    return weeks;
};

/* -------------------------------------------------------------
   Component
------------------------------------------------------------- */

const XPActivityCalendarCard = ({ streakString }) => {
    const data = parseStreakDataFor20Weeks(streakString);

    // Inject CSS for a visible scrollbar
    useEffect(() => {
        if (typeof document === 'undefined') return;
        const styleId = 'xp-calendar-scrollbar-style';
        if (!document.getElementById(styleId)) {
            const style = document.createElement('style');
            style.id = styleId;
            style.innerHTML = `
        .xp-calendar-scrollbar::-webkit-scrollbar { height: 8px; }
        .xp-calendar-scrollbar::-webkit-scrollbar { height: 8px !important; display: block !important; }
        .xp-calendar-scrollbar::-webkit-scrollbar-track { background: hsl(var(--muted)) !important; border-radius: 10px; }
        .xp-calendar-scrollbar::-webkit-scrollbar-thumb { background: hsl(var(--border)) !important; border-radius: 10px; }
        .xp-calendar-scrollbar::-webkit-scrollbar-thumb:hover { background: hsl(var(--muted-foreground)) !important; }
        /* For Firefox */
        .xp-calendar-scrollbar { scrollbar-width: thin !important; scrollbar-color: hsl(var(--border)) hsl(var(--muted)) !important; }

        .dark .xp-calendar-scrollbar::-webkit-scrollbar-track { background: hsl(var(--muted)); }
        .dark .xp-calendar-scrollbar::-webkit-scrollbar-thumb { background: hsl(var(--secondary)); }
        .dark .xp-calendar-scrollbar::-webkit-scrollbar-thumb:hover { background: hsl(var(--secondary-foreground)); }
        /* For Firefox - Dark Mode */
        .dark .xp-calendar-scrollbar {
          scrollbar-color: hsl(var(--secondary)) hsl(var(--muted)) !important;
        }

      `;
            document.head.appendChild(style);
        }
    }, []);

    const weeks = buildWeeks(data);

    const levelColor = level =>
        ({
            0: 'bg-neutral-200',
            1: 'bg-neutral-300',
            2: 'bg-green-200',
            3: 'bg-green-400',
            4: 'bg-green-600',
        })[level];

    return (
        <Card className="bg-transparent shadow-none flex lg:block flex-col md:flex-row md:pt-5 lg:pt-0">
            <CardHeader>
                <CardTitle>Activity Streak</CardTitle>
                <CardDescription>Your XP activity over the last 20 weeks.</CardDescription>
            </CardHeader>

            <CardContent className="flex-1 min-w-0">
                <div className="overflow-x-auto pb-2 xp-calendar-scrollbar min-w-0">
                    <TooltipProvider>
                        <div className="flex gap-1">
                            {weeks
                                .filter(week => week.some(d => d !== null))
                                .map((week, wi) => (
                                    <div key={wi} className="flex flex-col gap-1">
                                        {week.map((day, di) =>
                                            day ? (
                                                <Tooltip key={di}>
                                                    <TooltipTrigger asChild>
                                                        <div
                                                            className={`w-3 h-3 rounded-sm cursor-pointer transition-all hover:ring-2 hover:ring-neutral-400 ${levelColor(
                                                                day.level
                                                            )}`}
                                                        />
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        <div className="text-sm">
                                                            <div className="font-semibold">
                                                                {day.count} XP
                                                            </div>
                                                            <div className="text-white">
                                                                {new Date(
                                                                    day.date
                                                                ).toLocaleDateString('en-US', {
                                                                    weekday: 'short',
                                                                    month: 'short',
                                                                    day: 'numeric',
                                                                })}
                                                            </div>
                                                        </div>
                                                    </TooltipContent>
                                                </Tooltip>
                                            ) : (
                                                <div key={di} className="w-3 h-3 rounded-sm" />
                                            )
                                        )}
                                    </div>
                                ))}
                        </div>
                    </TooltipProvider>
                </div>

                {/* Legend */}
                <table className="mt-4 text-xs">
                    <tbody>
                        <tr className="text-neutral-700">
                            <td className="pr-2 align-middle">Less</td>
                            {[0, 1, 2, 3, 4].map(lvl => (
                                <td key={lvl} className="px-1">
                                    <div className={`w-3 h-3 rounded-sm ${levelColor(lvl)}`} />
                                </td>
                            ))}
                            <td className="pl-2 align-middle">More</td>
                        </tr>
                    </tbody>
                </table>
            </CardContent>
        </Card>
    );
};

export default XPActivityCalendarCard;
